import { create } from 'zustand';
import { DatabaseObject } from '../models/databaseObjectTypes';
import {
  DatabaseExplorerNode,
  createConnectionNode
} from '../../explorer/models/DatabaseTreeModels';
import {
  findNodeById,
  updateNodeInTree,
  addChildrenToNodeInTree
} from '../../explorer/services/treeService';
import {
  createConnectionNodeId
} from '../../explorer/utils/nodeIdUtils';
import {
  addDatabasesToTree,
  addDatabaseObjectsToTree
} from '../services/databaseTreeBuilder';
import { usePinnedObjectsStore } from './pinnedObjectsStore';

/**
 * Database store state and actions
 */
interface DatabaseState {
  // Tree data
  treeData: DatabaseExplorerNode[];
  
  // Loading states (can still be useful to have separate from tree for UI indicators)
  loadingConnections: Set<string>;
  loadingDatabases: Set<string>;
  loadingObjects: Record<string, Record<string, Set<string>>>;
  
  // Preloaded data 
  preloadedData: Record<string, {
    databases: string[];
    objects: Record<string, DatabaseObject[]>;
    columns: Record<string, Record<number, any[]>>;
    /**
     * Databases whose listing set was only PARTIALLY successful — at least one
     * object-type listing failed, so `objects[db]` is real but not the whole
     * story.
     *
     * Two kinds of reader need different answers, which is why this exists
     * rather than simply withholding the database. Surfaces that show what we
     * know (the tree, command-palette search, the first-connect bridge) are
     * better off with the tables that DID come back; consumers that treat an
     * entry as the definitive object list (`getPreloadedObjects`, and through
     * it the table / FK pickers) must not — F06. Withholding both is not an
     * option: on PostgreSQL 10 (the documented floor) both routine listings
     * filter on `pg_proc.prokind`, which is PG 11+, so EVERY database is
     * partial and the whole connection would go dark in search.
     */
    incomplete?: Record<string, true>;
    /**
     * Publication revision per database — bumped on every
     * {@link DatabaseState.mergePreloadedObjects}. A preload wave records the
     * revision it observed for each database it published, and
     * `setPreloadedData` skips any database whose revision has moved on since:
     * a per-folder Refresh that landed while the rest of the wave was still
     * running must not be reverted by the completion snapshot.
     */
    revisions?: Record<string, number>;
    isPreloading: boolean;
    preloadProgress: number;
    preloadStage: string;
  }>;
  
  // Tree actions
  setTreeData: (treeData: DatabaseExplorerNode[]) => void;
  updateNode: (nodeId: string, updates: Partial<DatabaseExplorerNode>) => void;
  toggleNodeExpanded: (nodeId: string) => boolean;
  addChildrenToNode: (parentId: string, children: DatabaseExplorerNode[]) => void;
  
  // Specific domain actions
  addConnection: (connectionId: string, name: string, metadata: any) => void;
  addDatabases: (connectionId: string, databaseNames: string[]) => void;
  addDatabaseObjects: (connectionId: string, database: string, objects: DatabaseObject[]) => void;
  removeConnection: (connectionId: string) => void;
  
  // Loading state actions
  setLoadingConnection: (connectionId: string, isLoading: boolean) => void;
  setLoadingDatabase: (connectionId: string, database: string, isLoading: boolean) => void;
  setLoadingObject: (connectionId: string, database: string, objectType: string, isLoading: boolean) => void;
  
  // Preloading actions
  updatePreloadProgress: (connectionId: string, stage: string, percent: number) => void;
  setPreloadedData: (connectionId: string, data: {
    databases: string[];
    objects: Record<string, any[]>;
    columns: Record<string, Record<number, any[]>>;
    /** Databases whose listing set was partial — see `preloadedData.incomplete`. */
    incomplete?: Record<string, true>;
    /** Revision this wave observed per database — see `preloadedData.revisions`. */
    revisions?: Record<string, number>;
    /**
     * Databases whose every listing rejected → mark the EXISTING entry
     * incomplete without touching its objects (value = the revision the wave
     * observed before its await; a newer publish keeps its completeness).
     */
    failed?: Record<string, number>;
  }) => void;
  /**
   * Publish ONE database's preloaded objects as soon as that database finishes,
   * without claiming the whole connection is done.
   *
   * `setPreloadedData` is the completion signal — it flips `isPreloading` to
   * false and progress to 100 — so it can only be called once, at the end. That
   * made every `preloadedData` reader (search, command palette, package
   * explorer, first-connect bridge) wait for the SLOWEST database: on Oracle the
   * system schemas (SYS, SYSTEM, …) push that past 30s, which is why the
   * package-explorer e2e specs were skipped. This merges a single database in
   * and leaves the completion flags alone.
   */
  mergePreloadedObjects: (
    connectionId: string,
    database: string,
    objects: DatabaseObject[],
    options?: {
      /**
       * Every object-type listing for this database fulfilled. `false` marks
       * the database incomplete, which makes {@link DatabaseState.getPreloadedObjects}
       * report `undefined` for it while the objects stay visible to readers
       * that iterate `objects` themselves. Defaults to `true`.
       */
      complete?: boolean;
    },
  ) => number;
  /**
   * A wave STARTED. Raises `isPreloading` without touching the objects already
   * published — called when the wave actually begins (not when it is queued),
   * so a wave queued behind another does not run with the flag its predecessor
   * lowered at completion.
   */
  beginPreload: (connectionId: string) => void;
  /**
   * A connect generation's preload is OVER — completed, or abandoned because
   * the connection went away. Clears `isPreloading` and keeps the objects: the
   * tree survives a disconnect, and a flag left stuck at `true` gates the next
   * listing, the folder expand and the connection-node toggle (the reconnect
   * then renders a skeleton forever).
   *
   */
  endPreload: (connectionId: string) => void;
  /** Current publication revision for a database (0 when never published). */
  getPreloadRevision: (connectionId: string, database: string) => number;
  isPreloading: (connectionId: string) => boolean;
  getPreloadedObjects: (connectionId: string, database: string) => DatabaseObject[] | undefined;
  
  // Clear actions
  clearTreeData: () => void;
  collapseAll: () => void;

  // Refresh action - clears databases folder children to force re-fetch
  clearDatabasesFolderChildren: (connectionId: string) => void;
}

export const useDatabaseStore = create<DatabaseState>((set, get) => ({
  // Initial state
  treeData: [],
  loadingConnections: new Set<string>(),
  loadingDatabases: new Set<string>(),
  loadingObjects: {},
  preloadedData: {},
  
  // Tree actions
  setTreeData: (treeData) => {
    set({ treeData });
  },
  
  updateNode: (nodeId, updates) => set((state) => ({
    treeData: updateNodeInTree(state.treeData, nodeId, updates)
  })),
  
  toggleNodeExpanded: (nodeId) => {
    let expandedAfterToggle = false;

    set((state) => {
      const node = findNodeById(nodeId, state.treeData);
      if (!node) {
        console.error(`Store: Node not found for ${nodeId}`);
        expandedAfterToggle = false;
        return state;
      }

      if (node.type === 'connection' && node.metadata) {
        const connectionId = node.metadata.id;
        if (connectionId && state.preloadedData[connectionId]?.isPreloading) {
          expandedAfterToggle = !!node.isExpanded;
          return state;
        }
      }

      expandedAfterToggle = !node.isExpanded;

      return {
        treeData: updateNodeInTree(state.treeData, nodeId, {
          isExpanded: expandedAfterToggle,
        }),
      };
    });

    return expandedAfterToggle;
  },
  
  addChildrenToNode: (parentId, children) => set((state) => ({
    treeData: addChildrenToNodeInTree(state.treeData, parentId, children)
  })),
  
  // Domain-specific actions
  addConnection: (connectionId, name, metadata) => set((state) => {
    const newConnection = createConnectionNode(connectionId, name, metadata);

    // Initialize the connection with its default structure (databases folder and security folders)
    // Pass engine to get engine-specific security folders
    const { ConnectionNodeBehaviors } = require('../../explorer/models/NodeBehaviors');
    newConnection.children = ConnectionNodeBehaviors.initializeDefaultStructure(newConnection, metadata?.engine);
    
    // Check if connection already exists
    const existing = state.treeData.find(node => 
      node.type === 'connection' && node.metadata.id === connectionId
    );
    
    if (existing) {
      return {
        treeData: updateNodeInTree(state.treeData, existing.id, {
          ...newConnection,
          // Preserve expanded state if it already exists
          isExpanded: existing.isExpanded
        })
      };
    }
    
    return {
      treeData: [...state.treeData, newConnection]
    };
  }),
  
  addDatabases: (connectionId, databaseNames) => set((state) => {
    // Use the centralized tree builder service to add databases to the tree
    try {
      const updatedTree = addDatabasesToTree(state.treeData, connectionId, databaseNames);
      return { treeData: updatedTree };
    } catch (error) {
      console.error(`Error adding databases to tree: ${error}`);
      return state;
    }
  }),
  
  addDatabaseObjects: (connectionId, database, objects) => set((state) => {
    // Use the centralized tree builder service to add database objects to the tree
    try {
      const updatedTree = addDatabaseObjectsToTree(state.treeData, connectionId, database, objects);
      return { treeData: updatedTree };
    } catch (error) {
      console.error(`Error adding database objects to tree: ${error}`);
      return state;
    }
  }),
  
  removeConnection: (connectionId) => set((state) => {
    // Release the pinned-objects slot + IPC `onChange` listener for this
    // connection (F15). Without this the byConnection entry survives the
    // disconnect with stale pins and a dangling listener that keeps firing
    // into a UI that no longer has a connection to render against.
    try {
      usePinnedObjectsStore.getState().forget(connectionId);
    } catch (err) {
      console.error(`[databaseStore.removeConnection] pinned-store forget failed for ${connectionId}:`, err);
    }

    // Filter out the connection node and any related nodes
    const filteredTree = state.treeData.filter(node => {
      // Check if this is the connection node itself
      if (node.type === 'connection' && node.metadata.id === connectionId) {
        return false;
      }
      
      // Check if this is a node related to the connection (e.g., has connection ID in its metadata)
      if (node.metadata && 'connectionId' in node.metadata && node.metadata.connectionId === connectionId) {
        return false;
      }
      
      return true;
    });
    
    // Also remove any preloaded data for this connection
    const newPreloadedData = { ...state.preloadedData };
    delete newPreloadedData[connectionId];
    
    return {
      treeData: filteredTree,
      preloadedData: newPreloadedData
    };
  }),
  
  // Loading state actions
  setLoadingConnection: (connectionId, isLoading) => set((state) => {
    const newLoadingConnections = new Set(state.loadingConnections);
    
    if (isLoading) {
      newLoadingConnections.add(connectionId);
    } else {
      newLoadingConnections.delete(connectionId);
    }
    
    return { loadingConnections: newLoadingConnections };
  }),
  
  setLoadingDatabase: (_connectionId, database, isLoading) => set((state) => {
    const newLoadingDatabases = new Set(state.loadingDatabases);
    
    if (isLoading) {
      newLoadingDatabases.add(database);
    } else {
      newLoadingDatabases.delete(database);
    }
    
    return { loadingDatabases: newLoadingDatabases };
  }),
  
  setLoadingObject: (connectionId, database, objectType, isLoading) => set((state) => {
    // Create a deep copy of the current loadingObjects
    const newLoadingObjects: Record<string, Record<string, Set<string>>> = { ...state.loadingObjects };
    
    // Initialize nested structure if it doesn't exist
    if (!newLoadingObjects[connectionId]) {
      newLoadingObjects[connectionId] = {};
    }
    
    if (!newLoadingObjects[connectionId][objectType]) {
      newLoadingObjects[connectionId][objectType] = new Set<string>();
    }
    
    // Update the set
    if (isLoading) {
      newLoadingObjects[connectionId][objectType].add(database);
    } else {
      newLoadingObjects[connectionId][objectType].delete(database);
    }
    
    return { loadingObjects: newLoadingObjects };
  }),
  
  // Preloading actions
    updatePreloadProgress: (connectionId, stage, percent) => set((state) => {
    // Make sure the connection exists in preloadedData
    if (!state.preloadedData[connectionId]) {
      return state;
    }
    
    // Update the connection node to show loading state with progress indicator
    const connectionNodeId = createConnectionNodeId(connectionId);
    const connectionNode = state.treeData.find(n => n.id === connectionNodeId);
    
    if (!connectionNode || !connectionNode.metadata) {
      return state;
    }
    
    // Update the node with loading state
    const updatedTree = updateNodeInTree(
      state.treeData, 
      connectionNodeId, 
      { 
        metadata: {
          ...connectionNode.metadata,
          connectionId,
          isLoading: percent < 100,
          loadingProgress: percent,
          loadingStage: stage
        }
      }
    );
    
    return {
      treeData: updatedTree,
      preloadedData: {
        ...state.preloadedData,
        [connectionId]: {
          ...state.preloadedData[connectionId],
          isPreloading: percent < 100,
          preloadProgress: percent,
          preloadStage: stage
        }
      }
    };
  }),
  
  setPreloadedData: (connectionId, data) => set((state) => {
    const existing = state.preloadedData[connectionId];
    const incomplete = { ...existing?.incomplete };
    const objects = { ...existing?.objects };

    for (const [database, published] of Object.entries(data.objects)) {
      // A database whose revision moved on since this wave published it has a
      // NEWER write — a per-folder Refresh that landed while the rest of the
      // wave was still running. Keep that write and its completeness mark; the
      // wave's own array is older, and for an empty result it would delete
      // freshly discovered objects outright.
      const observed = data.revisions?.[database];
      const current = existing?.revisions?.[database] ?? 0;
      if (observed !== undefined && current > observed) continue;

      objects[database] = published;
      if (data.incomplete?.[database]) incomplete[database] = true;
      else delete incomplete[database];
    }

    // Fully-failed databases: metadata-only. Keep the objects already there
    // (never erase what search / the tree / the bridge legitimately hold) but
    // mark the entry incomplete so `getPreloadedObjects` returns `undefined`
    // and the pickers refetch. No prior entry ⇒ nothing to mark. A newer
    // successful Refresh (higher revision) keeps its completeness — the stale
    // failure must not override it.
    for (const [database, baseline] of Object.entries(data.failed ?? {})) {
      if (objects[database] === undefined) continue;
      const current = existing?.revisions?.[database] ?? 0;
      if (current > baseline) continue;
      incomplete[database] = true;
    }

    return {
      preloadedData: {
        ...state.preloadedData,
        [connectionId]: {
          ...existing,
          databases: data.databases,
          columns: data.columns,
          objects,
          incomplete,
          isPreloading: false,
          preloadProgress: 100,
          preloadStage: 'Complete'
        }
      }
    };
  }),

  mergePreloadedObjects: (connectionId, database, objects, options) => {
    let published = 0;
    set((state) => {
    const existing = state.preloadedData[connectionId] ?? {
      databases: [],
      objects: {},
      columns: {},
      // IDLE. A merge does not start a preload — `beginPreload` does, and a
      // wave that is running has already raised the flag (this spread keeps
      // it). Creating the entry as "preloading" meant a one-off publish with
      // no wave behind it — the query editor's metadata fetch after a failed
      // listing is the everyday case — left `isPreloading` true with nothing
      // to clear it, gating the connection-node toggle and every ordinary
      // listing from then on.
      isPreloading: false,
      preloadProgress: 0,
      preloadStage: ''
    };
    published = (existing.revisions?.[database] ?? 0) + 1;
    const incomplete = { ...existing.incomplete };
    if (options?.complete === false) incomplete[database] = true;
    else delete incomplete[database];
    return {
      preloadedData: {
        ...state.preloadedData,
        [connectionId]: {
          ...existing,
          objects: { ...existing.objects, [database]: objects },
          incomplete,
          revisions: {
            ...existing.revisions,
            [database]: published,
          }
        }
      }
    };
    });
    return published;
  },
  
  beginPreload: (connectionId) => set((state) => {
    const existing = state.preloadedData[connectionId];
    return {
      preloadedData: {
        ...state.preloadedData,
        [connectionId]: {
          databases: existing?.databases ?? [],
          objects: existing?.objects ?? {},
          columns: existing?.columns ?? {},
          ...(existing?.incomplete ? { incomplete: existing.incomplete } : {}),
          ...(existing?.revisions ? { revisions: existing.revisions } : {}),
          preloadProgress: 0,
          preloadStage: 'Loading objects',
          isPreloading: true,
        },
      },
    };
  }),

  endPreload: (connectionId) => set((state) => {
    const existing = state.preloadedData[connectionId];
    if (!existing || !existing.isPreloading) return state;
    return {
      preloadedData: {
        ...state.preloadedData,
        [connectionId]: {
          ...existing,
          isPreloading: false,
          preloadProgress: 100,
          preloadStage: 'Complete',
        },
      },
    };
  }),

  getPreloadRevision: (connectionId, database) =>
    get().preloadedData[connectionId]?.revisions?.[database] ?? 0,

  isPreloading: (connectionId) => {
    const state = get();
    return !!state.preloadedData[connectionId]?.isPreloading;
  },
  
  getPreloadedObjects: (connectionId, database) => {
    const state = get();
    const entry = state.preloadedData[connectionId];
    // A partial set is not an answer for this accessor's callers: they treat
    // what they get as the complete object list (F06). Reporting `undefined`
    // sends them to their own fetch, while readers that walk `objects`
    // themselves still see what did come back.
    if (entry?.incomplete?.[database]) return undefined;
    return entry?.objects[database];
  },
  
  // Clear actions
  clearTreeData: () => set({
    treeData: [],
    loadingConnections: new Set<string>(),
    loadingDatabases: new Set<string>(),
    loadingObjects: {},
    preloadedData: {}
  }),

  collapseAll: () => set((state) => {
    const collapseNodes = <T extends DatabaseExplorerNode>(nodes: T[]): T[] => {
      return nodes.map(node => ({
        ...node,
        // Keep databases-folder always expanded
        isExpanded: node.type === 'databases-folder' ? true : false,
        children: node.children ? collapseNodes(node.children as DatabaseExplorerNode[]) : undefined,
      } as T));
    };

    return { treeData: collapseNodes(state.treeData) };
  }),

  clearDatabasesFolderChildren: (connectionId: string) => {
    const state = get();

    // Find the connection node
    const connectionNodeId = createConnectionNodeId(connectionId);
    const connectionNode = findNodeById(connectionNodeId, state.treeData);
    if (!connectionNode) {
      return;
    }

    // Find the databases-folder child
    const databasesFolder = connectionNode.children?.find(
      (child) => child.type === 'databases-folder'
    );
    if (!databasesFolder) {
      return;
    }

    // Clear children to force re-fetch on next expansion/refresh
    // Keep isExpanded state - the empty folder will prompt user to refresh
    set({
      treeData: updateNodeInTree(state.treeData, databasesFolder.id, {
        children: [],
        isLoading: false,
      }),
    });
  },
}));
